import { useEffect, useRef, useState } from "preact/hooks";
import type { SubmitFeedbackInput } from "../types";
import { collectBrowserInfo } from "../capture/browser";
import { snapshotConsole } from "../capture/console";
import { snapshotErrors } from "../capture/errors";
import { snapshotNetwork } from "../capture/network";
import {
  type AudioStatus,
  createRecordingSession,
  detectVideoExtension,
  formatDuration,
  type RecordingSession,
} from "../capture/recording";
import { getOrCreateClientId } from "../api/client-id";
import { submitFeedback, uploadMedia, type WidgetApiConfig } from "../api/client";
import { activeTeamCode, readProfile } from "../api/profile";
import { TypePicker, type FeedbackType } from "./TypePicker";
import { CloseIcon, SendIcon, StopIcon, VideoIcon } from "./icons";

type Phase = "idle" | "starting" | "recording" | "preview" | "submitting" | "success" | "error";

type Props = {
  apiConfig: WidgetApiConfig;
  onClose: () => void;
  onSubmitted?: () => void;
};

// Hard cap so a tab left recording for an hour doesn't OOM the browser or
// blow past the 100 MB upload limit (the server enforces 100 MB too).
const MAX_RECORDING_MS = 5 * 60_000;

export function RecordingMode({ apiConfig, onClose, onSubmitted }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioStatus, setAudioStatus] = useState<AudioStatus>({ mic: false, system: false });
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");

  const sessionRef = useRef<RecordingSession | null>(null);
  const startTimeRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (tickerRef.current) clearInterval(tickerRef.current);
      sessionRef.current?.cancel();
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function start() {
    setError(null);
    setPhase("starting");
    const session = createRecordingSession();
    sessionRef.current = session;
    try {
      await session.start();
      setAudioStatus(session.getAudioStatus());
      startTimeRef.current = performance.now();
      setElapsedMs(0);
      setPhase("recording");
      tickerRef.current = setInterval(() => {
        const ms = performance.now() - startTimeRef.current;
        setElapsedMs(ms);
        if (ms >= MAX_RECORDING_MS) {
          void stop();
        }
      }, 250);
      session.onStreamEnded(() => {
        // Browser-level "Stop sharing" — finalize as if user clicked Stop.
        void stop();
      });
    } catch (err) {
      sessionRef.current = null;
      const msg = err instanceof Error ? err.message : "Couldn't start recording";
      // User-cancelled permission throws NotAllowedError — treat as a soft
      // back-to-idle rather than a hard error so they can retry cleanly.
      if (err instanceof Error && err.name === "NotAllowedError") {
        setPhase("idle");
        return;
      }
      setError(msg);
      setPhase("error");
    }
  }

  async function stop() {
    const session = sessionRef.current;
    if (!session) return;
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    try {
      const blob = await session.stop();
      sessionRef.current = null;
      const url = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setVideoUrl(url);
      setPhase("preview");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to finish recording";
      setError(msg);
      setPhase("error");
    }
  }

  function retake() {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    setError(null);
    setPhase("idle");
  }

  async function submit() {
    if (!videoBlob) return;
    if (!title.trim()) {
      setError("Please add a title for this feedback.");
      return;
    }

    setPhase("submitting");
    setError(null);

    try {
      const ext = detectVideoExtension(videoBlob);
      const mediaId = await uploadMedia(apiConfig, videoBlob, `rekaman.${ext}`);

      const profile = readProfile();
      const reporterName = profile.name.trim() || undefined;
      const reporterEmail = email.trim() || undefined;

      const payload: Omit<SubmitFeedbackInput, "key"> = {
        type,
        severity: "medium",
        title: title.trim(),
        description: description.trim() || undefined,
        url: location.href,
        referrer: document.referrer || undefined,
        reporter: reporterName || reporterEmail ? { name: reporterName, email: reporterEmail } : undefined,
        code: activeTeamCode(profile),
        browserId: getOrCreateClientId(),
        mediaIds: [mediaId],
        diagnostics: {
          browser: collectBrowserInfo(),
          consoleLogs: snapshotConsole(),
          network: snapshotNetwork(),
          errors: snapshotErrors(),
          video: { durationMs: Math.round(elapsedMs) },
        },
      };

      await submitFeedback(apiConfig, payload);
      setPhase("success");
      onSubmitted?.();
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed");
      setPhase("preview");
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  if (phase === "success") {
    return (
      <div class="mf-overlay">
        <div class="mf-success mf-success-overlay">
          <div class="mf-success-icon">✓</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Thanks for the feedback!</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            Recording uploaded — we&apos;ll take a look.
          </div>
        </div>
      </div>
    );
  }

  if (phase === "idle" || phase === "starting") {
    return (
      <div class="mf-overlay mf-overlay-prompt" role="dialog" aria-label="Start screen recording">
        <button
          type="button"
          class="mf-tool mf-tool-prompt-close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        <div class="mf-prompt-card">
          <div class="mf-prompt-icon" aria-hidden="true">
            <VideoIcon />
          </div>
          <h2 class="mf-prompt-title">Record a video of the issue</h2>
          <p class="mf-prompt-desc">
            Your browser will ask which screen or tab to share, then to use your microphone so you
            can narrate. To also capture the page's own sound, tick “Share tab audio” in the share
            dialog. Recording stops automatically after 5 minutes.
          </p>
          {error && <div class="mf-error" style={{ marginBottom: 8 }}>{error}</div>}
          <div class="mf-actions">
            <button type="button" class="mf-btn mf-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              class="mf-btn mf-btn-primary"
              onClick={start}
              disabled={phase === "starting"}
            >
              {phase === "starting" ? "Waiting for permission…" : "Start recording"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "recording") {
    return (
      <div class="mf-recording-floating" role="status" aria-label="Recording">
        <div class="mf-recording-bar">
          <span class="mf-recording-dot" aria-hidden="true" />
          <span class="mf-recording-label">Recording…</span>
          <span
            class="mf-recording-mic"
            title={audioStatus.mic ? "Microphone is recording" : "No microphone — recording is silent"}
            aria-label={audioStatus.mic ? "Microphone on" : "Microphone off"}
          >
            {audioStatus.mic ? "🎤" : "🔇"}
          </span>
          <span class="mf-recording-timer">{formatDuration(elapsedMs)}</span>
          <button
            type="button"
            class="mf-btn mf-btn-primary"
            onClick={stop}
            style={{ marginLeft: 12 }}
          >
            <StopIcon /> Stop & preview
          </button>
        </div>
      </div>
    );
  }

  if (phase === "error" && !videoUrl) {
    return (
      <div class="mf-overlay mf-overlay-error" role="alert">
        <div class="mf-overlay-msg">{error ?? "Recording failed."}</div>
        <div class="mf-actions">
          <button type="button" class="mf-btn mf-btn-ghost" onClick={onClose}>
            Close
          </button>
          <button type="button" class="mf-btn mf-btn-primary" onClick={() => setPhase("idle")}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Preview / submitting phase — side-by-side: video left, form sidebar right.
  return (
    <div class="mf-overlay mf-overlay-preview">
      <div class="mf-overlay-toolbar">
        <button
          type="button"
          class="mf-tool"
          onClick={onClose}
          title="Cancel"
          aria-label="Cancel"
          disabled={phase === "submitting"}
        >
          <CloseIcon />
        </button>
      </div>

      <div class="mf-preview-main">
        <div class="mf-stage-video">
          {videoUrl ? (
            <video
              class="mf-video-preview"
              src={videoUrl}
              controls
              autoplay
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <div class="mf-video-fallback">Loading preview…</div>
          )}
        </div>

        <aside class="mf-preview-sidebar">
          {error && <div class="mf-error">{error}</div>}
          <TypePicker value={type} onChange={setType} disabled={phase === "submitting"} />
          <input
            class="mf-input"
            placeholder="What's the issue?"
            value={title}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            maxLength={200}
            disabled={phase === "submitting"}
          />
          <textarea
            class="mf-textarea"
            placeholder="More details (optional)"
            value={description}
            onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
            maxLength={4000}
            disabled={phase === "submitting"}
            rows={4}
          />
          <input
            class="mf-input"
            type="email"
            placeholder="Your email (optional)"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            disabled={phase === "submitting"}
          />
          <div class="mf-sidebar-meta">
            Recording · {formatDuration(elapsedMs)} ·{" "}
            {videoBlob ? `${(videoBlob.size / (1024 * 1024)).toFixed(1)} MB` : "—"} ·{" "}
            {audioStatus.mic
              ? "🎤 mic"
              : audioStatus.system
                ? "🔊 system audio"
                : "🔇 no audio"}
          </div>
          {!audioStatus.mic && !audioStatus.system && (
            <div class="mf-error" style={{ marginTop: 0 }}>
              No audio was captured. To record narration, allow microphone access and re-record.
            </div>
          )}
          <div class="mf-sidebar-actions">
            <button
              type="button"
              class="mf-btn mf-btn-ghost"
              onClick={retake}
              disabled={phase === "submitting"}
            >
              Re-record
            </button>
            <button
              type="button"
              class="mf-btn mf-btn-primary"
              onClick={submit}
              disabled={phase === "submitting" || !title.trim()}
            >
              {phase === "submitting" ? (
                "Uploading…"
              ) : (
                <>
                  <SendIcon /> Send feedback
                </>
              )}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
